import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Paper, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  IconButton,
  Alert,
  Card,
  CardContent,
  Collapse,
  Tab,
  Tabs,
  Chip,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  QrCode as QrCodeIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assignment as AssignIcon
} from '@mui/icons-material';
import { useInventory } from '../contexts/InventoryContext';
import { barcodeApi } from '../services/api.service';
import equipmentService from '../services/equipment.service';
import { useJob } from '../contexts/JobContext';
import { useInventoryPage } from '../contexts/InventoryPageContext';
import BarcodeScanner from './BarcodeScanner';

export default function AssignInventory({ 
  selectedEquipment = [], 
  hideBarcode = false, 
  mode = 'default',
  open = true,
  onClose,
  onAssignComplete 
}) {
  const { loading, error, clearError } = useInventory();
  const { jobList, currentJob, selectJob } = useJob();
  const { 
    selectedJobId, setSelectedJobId,
    selectedSiteId, setSelectedSiteId,
    sites, updateJobSites,
    handleBarcodeScan,
    lastScannedCode
  } = useInventoryPage();
  
  // Form state
  const [formData, setFormData] = useState({
    id: '',
    description: '',
    quantity: 1,
    notes: '',
    job: '',
    site: ''
  });
  
  // Items to assign
  const [itemsToAssign, setItemsToAssign] = useState([]);
  
  // Scanner state - auto-load scanner for barcode-first workflow
  const [showScanner, setShowScanner] = useState(true);
  
  // Search results
  const [searchResult, setSearchResult] = useState(null);
  
  // Removed tabs - now single assignment interface
  
  // Initialize form with current job and site from shared context
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      job: selectedJobId || (currentJob ? currentJob.id : ''),
      site: selectedSiteId
    }));
  }, [selectedJobId, selectedSiteId, currentJob]);
  
  // Listen for barcode scans from other components
  useEffect(() => {
    if (lastScannedCode && lastScannedCode !== formData.id) {
      setFormData(prev => ({
        ...prev,
        id: lastScannedCode
      }));
    }
  }, [lastScannedCode]);

  // Handle pre-selected equipment (when called from StatusHistoryDialog)
  useEffect(() => {
    if (selectedEquipment.length > 0 && mode === 'specific-equipment') {
      const equipment = selectedEquipment[0];
      // Use IdResolver for clean architecture
      const equipmentId = equipment.equipment_id || equipment.barcode || equipment.id;
      
      setFormData(prev => ({
        ...prev,
        id: equipmentId,
        description: equipment.description || '',
        serial_number: equipment.serial_number || '',
        quantity: 1
      }));
      
      // Auto-add to assignment list
      const assignmentItem = {
        id: equipmentId,
        description: equipment.description || `Equipment ${equipmentId}`,
        manufacturer: equipment.manufacturer || '',
        model: equipment.model || '',
        serial_number: equipment.serial_number || '',
        warehouse: equipment.warehouse_name || equipment.warehouse || '',
        rack: equipment.rack_name || equipment.rack || equipment.zone_name || equipment.zone || '',
        bay: equipment.bay_name || equipment.bay || '',
        quantity: 1,
        notes: ''
      };
      
      setItemsToAssign([assignmentItem]);
    }
  }, [selectedEquipment, mode]);
  
  // Look up item when item code changes (only if not pre-selected)
  useEffect(() => {
    // Skip lookup if we have pre-selected equipment or if in specific-equipment mode
    if ((selectedEquipment && selectedEquipment.length > 0) || mode === 'specific-equipment') {
      return;
    }
    
    const lookupItem = async () => {
      if (formData.id) {
        try {
          // Use V2 barcode lookup which supports equipment_id, barcode, and database ID
          const response = await barcodeApi.lookupByBarcode(formData.id);
          if (response.data && response.data.equipment) {
            const item = response.data.equipment;
            setSearchResult(item);
            setFormData(prev => ({
              ...prev,
              description: item.description || '',
              equipment_id: item.equipment_id || item.id
            }));
          } else {
            setSearchResult(null);
          }
        } catch (err) {
          console.error('Error looking up equipment:', err);
          setSearchResult(null);
        }
      } else {
        setSearchResult(null);
        setFormData(prev => ({
          ...prev,
          description: '',
          equipment_id: ''
        }));
      }
    };
    
    lookupItem();
  }, [formData.id]); // Remove searchInventory and selectedEquipment from deps to prevent loops
  
  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Update shared context and current job
    if (name === 'job') {
      setSelectedJobId(value);
      
      // Update current job when job selection changes
      const selectedJob = jobList.find(j => j.id === value);
      if (selectedJob) {
        selectJob(selectedJob);
      }
    } else if (name === 'site') {
      setSelectedSiteId(value);
    }
  };
  
  // Handle adding item to checkout list
  const handleAddItem = () => {
    // Validate form
    if (!formData.id) {
      alert('Item code is required');
      return;
    }
    
    // Description no longer required - it's part of the equipment
    
    // V2: No quantity validation - individual tracking only
    
    if (!formData.job) {
      alert('Job is required');
      return;
    }
    
    if (!formData.site) {
      alert('Site is required');
      return;
    }
    
    // Check if equipment is available or returned (V2 individual tracking)
    if (searchResult && searchResult.status !== 'available' && searchResult.status !== 'returned') {
      alert(`Equipment is not available. Current status: ${searchResult.status}`);
      return;
    }
    
    // Add equipment to assignment list (V2 individual items)
    setItemsToAssign([
      ...itemsToAssign,
      {
        ...formData,
        equipment_id: formData.equipment_id || formData.id,
        quantity: 1 // V2 uses individual items, so quantity is always 1
      }
    ]);
    
    // Reset form (except job and site)
    setFormData({
      id: '',
      description: '',
      quantity: 1,
      notes: '',
      job: formData.job,
      site: formData.site
    });
    
    // Reset search result
    setSearchResult(null);
  };
  
  // Handle removing item from assign list
  const handleRemoveItem = (index) => {
    setItemsToAssign(itemsToAssign.filter((_, i) => i !== index));
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      // For specific-equipment mode, itemsToAssign is pre-populated with the single equipment
      if (mode === 'specific-equipment') {
        if (itemsToAssign.length === 0 || !formData.job) {
          alert('Please select a job to assign this equipment');
          return;
        }
        
        // Assign the single equipment item using V2 API
        const item = itemsToAssign[0];
        await equipmentService.assignEquipment(formData.job, {
          equipment_id: String(item.equipment_id || item.id),
          siteId: formData.site ? String(formData.site) : undefined,
          assignedBy: 'user',
          notes: formData.notes || item.notes,
          forceAssign: true  // Skip validation check
        });
        
        // Call the completion callback if provided (parent will handle success notification)
        if (onAssignComplete) {
          onAssignComplete();
        }

        // Close the dialog if provided
        if (onClose) {
          onClose();
        }

        return;
      }
      
      // Normal mode - multiple items
      if (itemsToAssign.length === 0) {
        alert('Please add at least one equipment item to assign');
        return;
      }
      
      // Submit equipment assignments using V2 API
      const assignmentPromises = itemsToAssign.map(item => 
        equipmentService.assignEquipment(formData.job, {
          equipment_id: String(item.equipment_id || item.id),
          siteId: formData.site ? String(formData.site) : undefined,
          assignedBy: 'user',
          notes: item.notes,
          forceAssign: true  // Skip validation check
        })
      );
      
      await Promise.all(assignmentPromises);
      
      // Reset form and items list
      setItemsToAssign([]);
      alert('Equipment assigned successfully');
    } catch (err) {
      console.error('Error assigning equipment:', err);
    }
  };
  
  // Handle successful barcode scan
  const handleScanSuccess = (decodedText) => {
    // Set the scanned text as the item code
    setFormData(prev => ({
      ...prev,
      id: decodedText
    }));
    
    // Share the scanned code with other components
    handleBarcodeScan(decodedText);
    
    // Hide scanner after successful scan
    setShowScanner(false);
  };
  
  // Removed unused tab and site management functions
  
  // Get job and site names for display
  const getJobName = (jobId) => {
    const job = jobList.find(j => j.id === jobId);
    return job ? job.name : jobId;
  };
  
  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site ? site.name : siteId;
  };
  
  // Simplified - no tabs needed, single assignment interface

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { minHeight: '500px' } }}
    >
      <DialogTitle>
        <Typography variant="h5" component="h2">
          Assign Equipment to Job
        </Typography>
      </DialogTitle>
      <DialogContent>
        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}
      
      {/* Equipment Assignment Section */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={mode === 'specific-equipment' ? 12 : 6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 2 }}>
                {mode === 'specific-equipment' ? 'Assign Equipment' : 'Add Equipment to Assign'}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="job-label">Job</InputLabel>
                    <Select
                      labelId="job-label"
                      name="job"
                      value={formData.job}
                      onChange={handleChange}
                      label="Job"
                      required
                    >
                      {jobList.map(job => (
                        <MenuItem key={job.id} value={job.id}>
                          {job.name} ({job.id})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="site-label">Site (Optional)</InputLabel>
                    <Select
                      labelId="site-label"
                      name="site"
                      value={formData.site}
                      onChange={handleChange}
                      label="Site"
                    >
                      <MenuItem value="">
                        <em>No Site</em>
                      </MenuItem>
                      {sites.map(site => (
                        <MenuItem key={site.id} value={site.id}>
                          {site.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select a site if this equipment is for a specific location
                    </FormHelperText>
                  </FormControl>
                </Grid>
                
                {/* Conditionally show Item Code/Barcode field based on mode */}
                {!hideBarcode && (
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center">
                      <TextField 
                        name="id"
                        label="QR Code / Equipment ID"
                        value={formData.id}
                        onChange={handleChange}
                        fullWidth
                        required
                        margin="normal"
                      />
                      <IconButton 
                        color="primary" 
                        onClick={() => setShowScanner(!showScanner)}
                        sx={{ ml: 1 }}
                      >
                        <QrCodeIcon />
                      </IconButton>
                    </Box>
                    
                    {/* Barcode Scanner */}
                    <Collapse in={showScanner}>
                      <Box mt={2} mb={2}>
                        <BarcodeScanner 
                          onScanSuccess={handleScanSuccess}
                          onError={(err) => console.error('Scan error:', err)}
                        />
                      </Box>
                    </Collapse>
                  </Grid>
                )}
                
                {/* Show equipment details when pre-selected */}
                {hideBarcode && selectedEquipment.length > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        ID: {selectedEquipment[0].equipment_id || selectedEquipment[0].id}
                      </Typography>
                      {selectedEquipment[0].serial_number && (
                        <Typography variant="body2" color="text.secondary">
                          Serial: {selectedEquipment[0].serial_number}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}
                
                {searchResult && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Found:</strong> {searchResult.description}
                      </Typography>
                      {searchResult.manufacturer && (
                        <Typography variant="body2">
                          <strong>Manufacturer:</strong> {searchResult.manufacturer}
                        </Typography>
                      )}
                      {searchResult.model && (
                        <Typography variant="body2">
                          <strong>Model:</strong> {searchResult.model}
                        </Typography>
                      )}
                      {searchResult.serial_number && (
                        <Typography variant="body2">
                          <strong>Serial Number:</strong> {searchResult.serial_number}
                        </Typography>
                      )}
                      {searchResult.barcode && (
                        <Typography variant="body2">
                          <strong>Barcode:</strong> {searchResult.barcode}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        <strong>Status:</strong> {searchResult.status || 'available'}
                      </Typography>
                      {searchResult.warehouse && (
                        <Typography variant="body2">
                          <strong>Location:</strong> {searchResult.warehouse} - {searchResult.rack || searchResult.zone} {searchResult.bay ? `- ${searchResult.bay}` : ''}
                        </Typography>
                      )}
                    </Alert>
                  </Grid>
                )}
                
                {mode === 'specific-equipment' && (
                  <Grid item xs={12}>
                    <TextField 
                      name="serialNumber"
                      label="Serial Number"
                      value={formData.serialNumber || selectedEquipment[0]?.serial_number || selectedEquipment[0]?.serialNumber || ''}
                      disabled
                      fullWidth
                      margin="normal"
                      helperText="Serial number of the individual equipment item"
                    />
                  </Grid>
                )}
                
                {/* V2: Quantity field removed - individual equipment tracking only */}
                
                <Grid item xs={12} sm={6}>
                  <TextField 
                    name="notes"
                    label="Notes"
                    value={formData.notes}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={mode === 'specific-equipment' ? handleSubmit : handleAddItem}
                    startIcon={<AssignIcon />}
                    fullWidth
                    disabled={!formData.job || !formData.id}
                    sx={{ mt: 2 }}
                  >
                    {mode === 'specific-equipment' ? 'Assign' : 'Add to Assignment List'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          
          {mode !== 'specific-equipment' && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" component="h3" gutterBottom sx={{ mb: 2 }}>
                  Equipment to Assign
                </Typography>
              
              {itemsToAssign.length === 0 ? (
                <Box py={3} textAlign="center">
                  <Typography variant="body1" color="textSecondary">
                    No equipment added to assignment list yet.
                  </Typography>
                </Box>
              ) : (
                <>
                  <List>
                    {itemsToAssign.map((item, index) => (
                      <Box key={index} mb={1}>
                        <Card variant="outlined">
                          <CardContent sx={{ pb: 1 }}>
                            <Grid container alignItems="center">
                              <Grid item xs>
                                <Typography variant="subtitle1" component="div">
                                  {item.description}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Equipment ID: {item.equipment_id || item.id}
                                </Typography>
                                {(searchResult?.manufacturer || item.manufacturer) && (
                                  <Typography variant="body2" color="textSecondary">
                                    {searchResult?.manufacturer || item.manufacturer} {searchResult?.model || item.model || ''}
                                  </Typography>
                                )}
                                {(searchResult?.serial_number || item.serial_number) && (
                                  <Typography variant="body2" color="textSecondary">
                                    S/N: {searchResult?.serial_number || item.serial_number}
                                  </Typography>
                                )}
                                <Box display="flex" alignItems="center" mt={1}>
                                  <Chip 
                                    icon={<BusinessIcon />} 
                                    label={getJobName(item.job)} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ mr: 1 }}
                                  />
                                  <Chip
                                    icon={<LocationIcon />}
                                    label={getSiteName(item.site)}
                                    size="small"
                                    color="primary"
                                    sx={{ mr: 1 }}
                                  />
                                  {/* V2: Individual tracking - no quantity display */}
                                </Box>
                              </Grid>
                              <Grid item>
                                <IconButton 
                                  edge="end" 
                                  aria-label="delete" 
                                  onClick={() => handleRemoveItem(index)}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Box>
                    ))}
                  </List>
                  
                </>
              )}
            </Paper>
          </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Processing...' : 'Assign All Equipment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}